import { Alert, message, Spin } from "antd";
import React, { useEffect, useState } from "react";

import { controller } from "../../../../controller";

import BreakDownUploadedFile from "./BreakDownUploadedFile";
import AddPatientModal from "../AddPatientModal";
import ProcedureSections from "./sheet/ProcedureSections";
import LineEditDrawer from "./sheet/LineEditDrawer";

// INFO: `breakdown` is what that was returned in the `doctor-note-lc` endpoint
// INFO: `breakdownData` is the breakdown data that we refetch after editing or adding a new procedure
// INFO: the `breakdownData` is just the same as `breakdown` but it has data after an action like editing/adding a procedure
export default function BreakdownDetailsModal({
  isGettingDoctorNote,
  patientPolicies,
  eligibilityData,
  agentType,
  breakdown,
  patient,
  newBreakdownID,
  setNewBreakdownID,
  selectedPolicyID,
  isBreakdownEligibilityRunning = false,
  eligibilityAgent2FAStatus = "",
  eligibilityTaskStatus = "",
  onNoteSaved,
  onProceduresChanged,
  // The plan's figures and policy, for the procedure sections (category %s,
  // shared maximums) — held by InsuranceBreakdownMasterModal.
  categories,
  sharedMaxGroups,
  policy,
  // FTC personnel see the intake cues (the "Not available" fill-in links).
  intake = false,
}) {
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [isLoadingInsuranceData, setIsLoadingBreakdownsData] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownStatus, setBreakdownStatus] = useState(undefined);
  // The line drawer: { row } to edit one line, { row: null } to add one.
  const [lineDrawer, setLineDrawer] = useState(null);
  // The selected policy object — carries per-policy outcome state (verified /
  // needs-attention) + its note, so the OutcomeTagControl buttons reflect THIS
  // policy rather than the patient's rollup tags.
  const selectedPolicyObj = (patientPolicies || []).find((x) => x.id === selectedPolicyID);

  // SECTION: Reactions
  // Setting breakdown status
  useEffect(function () {
    if (!breakdownData || breakdownData.status === "processing") return;
    setBreakdownStatus(breakdownData.status);
  }, [breakdownData]);

  // Populate table from AI breakdown agent response (new breakdown object + lines)
  useEffect(
    function () {
      if (agentType === "breakdown" && eligibilityData !== undefined && eligibilityData.patient_procedure_coverages) {
        setTableData(
          eligibilityData.patient_procedure_coverages.filter(
            (procedureCoverage) => procedureCoverage.insurance_policy === selectedPolicyID,
          ),
        );
        setBreakdownData(eligibilityData);
      }
    },
    [eligibilityData, agentType, selectedPolicyID],
  );

  // Fetch the insurance breakdownData when a specific breakdown is selected
  useEffect(
    function () {
      if (newBreakdownID) {
        getBreakdownsDetailsData();
      }
    },
    [newBreakdownID],
  );

  // SECTION: Functions
  async function getBreakdownsDetailsData() {
    try {
      setIsLoadingBreakdownsData(true);
      await getInsuranceBreakdownDetails();
    } catch (err) {
      message.error("Something went wrong while getting insurance breakdown details.");
      console.error(err);
    } finally {
      setIsLoadingBreakdownsData(false);
    }
  }

  // The procedures table calls this after add/edit/delete: refetch the rows,
  // then notify the parent so the coverage cards recompute for this breakdown.
  async function handleProceduresChanged() {
    await getBreakdownsDetailsData();
    if (typeof onProceduresChanged === "function") onProceduresChanged();
  }

  // Fetch the breakdown-specific procedure coverages and files
  const getInsuranceBreakdownDetails = async () => {
    if (newBreakdownID) {
      const response = await controller.getInsuranceBreakdownsPatientDetails(newBreakdownID);

      if (response.status < 250) {
        setTableData(response.json.patient_procedure_coverages || []);
      }

      setBreakdownData(response.json);
    }
  };

  return (
    <>
      <div style={{ padding: 0, width: "100%", height: "100%", minWidth: 0 }}>
        {isLoadingInsuranceData || isGettingDoctorNote ? (
          <div style={{ width: "100%", height: 200, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, gap: 10 }}>
            <Spin />
            loading...
          </div>
        ) : (
          <>
            <div style={{ paddingBottom: 24, width: "100%", boxSizing: "border-box", minWidth: 0 }}>
              <div style={{ width: "100%", minWidth: 0 }}>

                {isBreakdownEligibilityRunning &&
                  eligibilityTaskStatus !== "done" &&
                  eligibilityTaskStatus !== "failed" &&
                  (eligibilityAgent2FAStatus === "pending" ||
                    eligibilityAgent2FAStatus === "") && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ margin: "14px 24px 0" }}
                      message="Waiting on the insurance portal"
                      description={
                        <>
                          The agent is still connecting or signing in. <strong>pending</strong> in the API is normal until
                          the carrier shows a verification step. When a code is required, status becomes{" "}
                          <strong>waiting</strong> and a separate popup opens (it may appear above this window). This
                          can take several minutes.
                        </>
                      }
                    />
                  )}

                {isBreakdownEligibilityRunning &&
                  eligibilityTaskStatus !== "done" &&
                  eligibilityTaskStatus !== "failed" &&
                  String(eligibilityAgent2FAStatus).toLowerCase() === "waiting" && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ margin: "14px 24px 0" }}
                      message="Verification code needed"
                      description="A popup should be open to enter your two-factor code. If you do not see it, check for another browser window or tab, or move this window aside—it uses a higher layer than this breakdown dialog."
                    />
                  )}

                <ProcedureSections
                  rows={tableData}
                  categories={categories}
                  sharedMaxGroups={sharedMaxGroups}
                  policy={policy || selectedPolicyObj}
                  carrierName={(policy || selectedPolicyObj || {}).insurance_name_display}
                  canAdd={!!selectedPolicyID}
                  intake={intake}
                  onEdit={(row) => setLineDrawer({ row })}
                  onAdd={() => setLineDrawer({ row: null })}
                />
              </div>

              <LineEditDrawer
                open={!!lineDrawer}
                row={lineDrawer ? lineDrawer.row : null}
                breakdownId={newBreakdownID || (breakdown && breakdown.id)}
                policyId={selectedPolicyID}
                patientId={patient && patient.id}
                policy={policy || selectedPolicyObj}
                rows={tableData}
                onClose={() => setLineDrawer(null)}
                onSaved={(saved, { created }) => {
                  setLineDrawer(null);
                  // Adding a line with no breakdown open creates one.
                  if (created && !newBreakdownID && saved && saved.insurance_breakdown && setNewBreakdownID) {
                    setNewBreakdownID(saved.insurance_breakdown);
                    return;
                  }
                  handleProceduresChanged();
                }}
                onDeleted={() => { setLineDrawer(null); handleProceduresChanged(); }}
              />

              {breakdownData && breakdownData.files && breakdownData.files.length > 0 && (
                <div style={{ margin: "24px 24px 0" }}>
                  <p style={{ fontSize: 18 }}>Uploaded Files</p>

                  <div style={{ display: "flex", alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
                    {breakdownData.files.map((file, index) => (
                      <BreakDownUploadedFile key={index} file={file} />
                    ))}
                  </div>
                </div>
              )}

              {/* Modal for adding patient */}
              <AddPatientModal showAddPatient={showAddPatient} setShowAddPatient={setShowAddPatient} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
